import { AppexUserScope, useAuthScopes } from '@/lib/useAuthScopes';
import { goToCurrent } from '@/helpers/routing/links';
import { EnumPermission } from '@/helpers/auth/permissions';
import { User } from '@/models/user';
import { NavigationGuard, NavigationGuardNext, Route } from 'vue-router/types/router';

type GuardFailureHandler = string | (() => void);

const { activeScope } = useAuthScopes();

export const isSuperAdmin = (user?: User) => {
    if (!user) {
        return false;
    }

    if (activeScope.value && activeScope.value.role && activeScope.value.role.name !== 'Superadmin') {
        return false;
    }

    return user.is_superadmin;
};

// Internal helper
function handlePermissionCheck(result: boolean, next: NavigationGuardNext, redirectTo: string) {
    if (result) {
        next();
    } else {
        next(redirectTo);
    }
}

export const hasPermissionRouteGuard = (
    permission: EnumPermission | EnumPermission[],
    redirectTo: string = '/home'
) => {
    return (to: Route, from: Route, next: NavigationGuardNext) => {
        handlePermissionCheck(hasPermission(permission), next, redirectTo);
    };
};

export const hasAnyPermissionRouteGuard = (permissions: EnumPermission[], redirectTo: string = '/home') => {
    return (to: Route, from: Route, next: NavigationGuardNext) => {
        handlePermissionCheck(hasAnyPermission(permissions), next, redirectTo);
    };
};

export const directHasPermissionRouteGuard = (
    next: NavigationGuardNext,
    permission: EnumPermission | EnumPermission[],
    redirectTo: string = '/home'
) => {
    handlePermissionCheck(hasPermission(permission), next, redirectTo);
};

export const directHasAnyPermissionRouteGuard = (
    next: NavigationGuardNext,
    permissions: EnumPermission[],
    redirectTo: string = '/home'
) => {
    handlePermissionCheck(hasAnyPermission(permissions), next, redirectTo);
};

export const isSuperAdminNavigationGuard = (user?: User, onFail?: GuardFailureHandler) => {
    const ok = isSuperAdmin(user);

    if (!ok) {
        handleGuardFailure(onFail);
    }

    return ok;
};

export const hasPermissionNavigationGuard = async (
    permission: EnumPermission | EnumPermission[],
    onFail?: GuardFailureHandler
): Promise<boolean> => {
    const ok = hasPermissionForUser(activeScope.value, permission);

    if (!ok) {
        handleGuardFailure(onFail);
    }

    return ok;
};

export const hasAnyPermissionNavigationGuard = async (
    permissions: EnumPermission[],
    onFail?: GuardFailureHandler
): Promise<boolean> => {
    const ok = hasAnyPermissionForUser(activeScope.value, permissions);

    if (!ok) {
        handleGuardFailure(onFail);
    }

    return ok;
};

const handleGuardFailure = (handler?: GuardFailureHandler) => {
    if (typeof handler === 'function') {
        handler();
    } else if (typeof handler === 'string') {
        window.location.href = handler;
    } else {
        goToCurrent('/home');
    }
};

export const hasPermission = (permission: EnumPermission | EnumPermission[]): boolean => {
    return hasPermissionForUser(activeScope.value, permission);
};

export const hasAnyPermission = (permissions: EnumPermission[]): boolean => {
    return hasAnyPermissionForUser(activeScope.value, permissions);
};

export const hasPermissionForUser = (
    scope: AppexUserScope | undefined,
    permission: EnumPermission | EnumPermission[]
): boolean => {
    const keys = Array.isArray(permission) ? permission : [permission];
    return checkPermissions(scope, keys, true);
};

export const hasAnyPermissionForUser = (scope: AppexUserScope | undefined, permissions: EnumPermission[]): boolean => {
    return checkPermissions(scope, permissions, false);
};

const checkPermissions = (scope: AppexUserScope | undefined, keys: EnumPermission[], allRequired: boolean): boolean => {
    if (!scope || !scope.role || !scope.role.permissions) {
        return false;
    }

    const userPermissions = scope.role.permissions.map((permission) => permission.key);

    return allRequired
        ? keys.every((permission) => userPermissions.includes(permission))
        : keys.some((permission) => userPermissions.includes(permission));
};
